import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import {
  createPaymentOrder,
  verifyPayment as verifyPaymentAPI
} from "./paymentService";


// ===================== DEV LOGGER =====================
const devLog = (...args) => {
  if (process.env.NODE_ENV === "development") {
    console.log("[CheckoutPage]", ...args);
  }
};

const CheckoutPage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [alert, setAlert] = useState(null);
  const [redirectCounter, setRedirectCounter] = useState(null);
  const [progress, setProgress] = useState(0);

  const redirectIntervalRef = useRef(null);

  // ✅ StrictMode protection
  const checkoutInitiatedRef = useRef(false);

  const checkoutStartedKey = `checkout_started_${planId}`;

  // ===================== ENTRY VALIDATION =====================
  useEffect(() => {
    devLog("Component mounted", { planId, user });

    if (!user) {
      devLog("User not logged in → redirecting to login");
      navigate("/login", { replace: true });
      return;
    }

    if (!planId) {
      devLog("Invalid planId → redirecting to plans");
      navigate("/plans", { replace: true });
      return;
    }

    // ✅ StrictMode duplicate prevention
    if (checkoutInitiatedRef.current) {
      devLog("Checkout already initiated (ref) → skipping");
      return;
    }

    // ✅ Refresh / back button protection
    if (sessionStorage.getItem(checkoutStartedKey)) {
      devLog("Checkout already started (sessionStorage) → skipping");
      checkoutInitiatedRef.current = true;
      return;
    }

    // ✅ Mark both protections
    checkoutInitiatedRef.current = true;
    sessionStorage.setItem(checkoutStartedKey, "true");

    devLog("Starting checkout flow");

    initCheckout();

    return () => {
      devLog("Cleanup → clearing redirect interval");
      clearInterval(redirectIntervalRef.current);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, user]);

  // ===================== ALERT + REDIRECT =====================
  const showAlert = (type, message, redirectPath = null, delay = 4000) => {
    devLog("Showing alert", { type, message, redirectPath });

    setAlert({ type, message });

    if (redirectPath) {
      setRedirectCounter(Math.ceil(delay / 1000));
      setProgress(0);

      clearInterval(redirectIntervalRef.current);

      redirectIntervalRef.current = setInterval(() => {
        setRedirectCounter((prev) => {
          if (prev <= 1) {
            devLog("Redirecting →", redirectPath);

            clearInterval(redirectIntervalRef.current);
            navigate(redirectPath, { replace: true });

            return 0;
          }
          return prev - 1;
        });

        setProgress((prev) =>
          Math.min(prev + 100 / (delay / 1000), 100)
        );
      }, 1000);
    }
  };

  // ===================== LOAD RAZORPAY =====================
  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      devLog("Loading Razorpay SDK");

      // prevent duplicate script injection
      if (window.Razorpay) {
        devLog("Razorpay SDK already loaded");
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;

      script.onload = () => {
        devLog("Razorpay SDK loaded successfully");
        resolve(true);
      };

      script.onerror = () => {
        devLog("Razorpay SDK failed to load");
        resolve(false);
      };

      document.body.appendChild(script);
    });

  // ===================== INIT CHECKOUT =====================
  const initCheckout = async () => {
    devLog("Initializing checkout");

    const sdkLoaded = await loadRazorpayScript();

    if (!sdkLoaded) {
      showAlert(
        "error",
        "Payment service failed to load. Try again.",
        "/plans"
      );
      return;
    }

    try {
      const payload = {
        planId: Number(planId),
        userId: user.userId,
      };

      devLog("Creating order", payload);

      const orderResponse = await createPaymentOrder(payload);

      devLog("Order created", orderResponse.data);

      const { orderId, amount, currency, planName } =
        orderResponse.data;

      openCheckout(orderId, amount, currency, planName);

    } catch (error) {
      devLog("Order creation failed", error);

      sessionStorage.removeItem(checkoutStartedKey);
      checkoutInitiatedRef.current = false;

      showAlert(
        "error",
        "Unable to start payment. Try again.",
        "/plans"
      );
    }
  };

  // ===================== OPEN CHECKOUT =====================
  const openCheckout = (orderId, amount, currency, planName) => {
    devLog("Opening Razorpay", { orderId, amount });

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,

      amount,
      currency,

      name: "Speak Bold with KK",
      description: planName || "Plan Subscription",

      order_id: orderId,

      prefill: {
        name: user?.name || "",
        email: user?.email || "",
      },

      notes: {
        userId: user.userId,
        planId: Number(planId),
      },

      handler: (response) => {
        devLog("Payment success", response);
        verifyPayment(response);
      },

      modal: {
        ondismiss: () => {
          devLog("Checkout dismissed");

          sessionStorage.removeItem(checkoutStartedKey);
          checkoutInitiatedRef.current = false;

          showAlert(
            "info",
            "Payment cancelled. Redirecting...",
            "/plans"
          );
        },
      },

      theme: {
        color: "#007BFF",
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  // ===================== VERIFY PAYMENT =====================
  const verifyPayment = async (paymentResponse) => {
    devLog("Verifying payment", paymentResponse);

    try {
      const payload = {
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        planId: Number(planId),
        userId: user.userId,
      };

       const response = await verifyPaymentAPI(payload);

      devLog("Verification response", response.data);

      handlePaymentResult(
        response.data.status,
        response.data.message
      );

    } catch (error) {
      devLog("Verification failed", error);

      handlePaymentResult(
        "ERROR",
        "Payment verification pending."
      );
    }
  };

  // ===================== HANDLE RESULT =====================
  const handlePaymentResult = (status, message) => {
    devLog("Payment result", { status, message });

    sessionStorage.removeItem(checkoutStartedKey);
    checkoutInitiatedRef.current = false;

    if (status === "SUCCESS" || status === "ALREADY_VERIFIED") {
      showAlert(
        "success",
        "🎉 Payment successful!",
        "/my-active-plan"
      );
    } else {
      showAlert(
        "error",
        message || "Payment failed.",
        "/plans"
      );
    }
  };

  // ===================== UI =====================
  return (
    <div style={{ textAlign: "center", marginTop: "80px" }}>
      {alert && (
        <AlertBanner
          type={alert.type}
          message={alert.message}
        />
      )}

      <h3>🔐 Processing your payment</h3>
      <p>Please don’t refresh or press back.</p>

      {redirectCounter !== null && (
        <>
          <p>Redirecting in {redirectCounter}s</p>
          <ProgressBar progress={progress} />
        </>
      )}
    </div>
  );
};

// ===================== ALERT =====================
const AlertBanner = ({ type, message }) => {
  const colors = {
    success: "#16a34a",
    error: "#dc2626",
    warning: "#d97706",
    info: "#2563eb",
  };

  return (
    <div
      style={{
        background: colors[type],
        color: "#fff",
        padding: "12px",
        borderRadius: "8px",
        marginBottom: "20px",
        maxWidth: "480px",
        marginInline: "auto",
      }}
    >
      {message}
    </div>
  );
};

// ===================== PROGRESS =====================
const ProgressBar = ({ progress }) => (
  <div
    style={{
      width: "300px",
      height: "8px",
      background: "#e5e7eb",
      borderRadius: "4px",
      margin: "12px auto",
    }}
  >
    <div
      style={{
        width: `${progress}%`,
        height: "100%",
        background: "#007BFF",
      }}
    />
  </div>
);

export default CheckoutPage;
